// src/components/common/SearchBar.jsx
import React, { useState, useEffect, useCallback, useRef } from 'react';

const SearchBar = ({ 
  onSearch, 
  placeholder = 'Search...', 
  delay = 500,
  size = 'md',
  variant = 'default',
  fullWidth = false,
  autoFocus = false,
  showClearButton = true,
  showSearchIcon = true,
  showFilterButton = false,
  onFilterClick,
  filterCount = 0,
  initialValue = '',
  disabled = false,
}) => {
  const [searchTerm, setSearchTerm] = useState(initialValue);
  const [isFocused, setIsFocused] = useState(false);
  const inputRef = useRef(null);
  const timerRef = useRef(null);

  // Size mappings
  const sizes = {
    sm: {
      container: {
        height: '2rem',
      },
      input: {
        padding: '0.25rem 0.75rem',
        paddingLeft: showSearchIcon ? '2.25rem' : '0.75rem',
        paddingRight: showClearButton ? '2.25rem' : '0.75rem',
        fontSize: '0.75rem',
      },
      icon: {
        width: '1rem',
        height: '1rem',
      },
      clearButton: {
        width: '1.25rem',
        height: '1.25rem',
        fontSize: '0.875rem',
      },
      filterButton: {
        padding: '0 0.5rem',
        height: '2rem',
        fontSize: '0.75rem',
      },
    },
    md: {
      container: {
        height: '2.5rem',
      },
      input: {
        padding: '0.5rem 1rem',
        paddingLeft: showSearchIcon ? '2.75rem' : '1rem',
        paddingRight: showClearButton ? '2.75rem' : '1rem',
        fontSize: '0.875rem',
      },
      icon: {
        width: '1.25rem',
        height: '1.25rem',
      },
      clearButton: {
        width: '1.5rem',
        height: '1.5rem',
        fontSize: '1rem',
      },
      filterButton: {
        padding: '0 0.75rem',
        height: '2.5rem',
        fontSize: '0.875rem',
      },
    },
    lg: {
      container: {
        height: '3rem',
      },
      input: {
        padding: '0.75rem 1.25rem',
        paddingLeft: showSearchIcon ? '3.25rem' : '1.25rem',
        paddingRight: showClearButton ? '3.25rem' : '1.25rem',
        fontSize: '1rem',
      },
      icon: {
        width: '1.5rem',
        height: '1.5rem',
      },
      clearButton: {
        width: '1.75rem',
        height: '1.75rem',
        fontSize: '1.125rem',
      },
      filterButton: {
        padding: '0 1rem',
        height: '3rem',
        fontSize: '1rem',
      },
    },
  };

  // Variant styles
  const variants = {
    default: {
      container: {
        backgroundColor: '#ffffff',
        borderColor: '#d1d5db',
        borderWidth: '1px',
        borderStyle: 'solid',
      },
      containerFocused: {
        borderColor: '#3b82f6',
        boxShadow: '0 0 0 3px rgba(59, 130, 246, 0.1)',
      },
      input: {
        color: '#111827',
        backgroundColor: 'transparent',
      },
      inputPlaceholder: {
        color: '#9ca3af',
      },
      icon: {
        color: '#9ca3af',
      },
      iconHover: {
        color: '#6b7280',
      },
      clearButton: {
        color: '#9ca3af',
        backgroundColor: '#f3f4f6',
      },
      clearButtonHover: {
        color: '#111827',
        backgroundColor: '#e5e7eb',
      },
    },
    outline: {
      container: {
        backgroundColor: 'transparent',
        borderColor: '#d1d5db',
        borderWidth: '1px',
        borderStyle: 'solid',
      },
      containerFocused: {
        borderColor: '#3b82f6',
        borderWidth: '2px',
      },
      input: {
        color: '#111827',
        backgroundColor: 'transparent',
      },
      inputPlaceholder: {
        color: '#9ca3af',
      },
      icon: {
        color: '#9ca3af',
      },
      iconHover: {
        color: '#3b82f6',
      },
      clearButton: {
        color: '#9ca3af',
        backgroundColor: 'transparent',
      },
      clearButtonHover: {
        color: '#ef4444',
      },
    },
    filled: {
      container: {
        backgroundColor: '#f3f4f6',
        borderColor: 'transparent',
        borderWidth: '1px',
        borderStyle: 'solid',
      },
      containerFocused: {
        backgroundColor: '#ffffff',
        borderColor: '#3b82f6',
      },
      input: {
        color: '#111827',
        backgroundColor: 'transparent',
      },
      inputPlaceholder: {
        color: '#9ca3af',
      },
      icon: {
        color: '#9ca3af',
      },
      iconHover: {
        color: '#6b7280',
      },
      clearButton: {
        color: '#9ca3af',
        backgroundColor: '#e5e7eb',
      },
      clearButtonHover: {
        color: '#111827',
        backgroundColor: '#d1d5db',
      },
    },
    underlined: {
      container: {
        backgroundColor: 'transparent',
        borderBottomColor: '#d1d5db',
        borderBottomWidth: '1px',
        borderBottomStyle: 'solid',
        borderRadius: '0',
      },
      containerFocused: {
        borderBottomColor: '#3b82f6',
        borderBottomWidth: '2px',
      },
      input: {
        color: '#111827',
        backgroundColor: 'transparent',
      },
      inputPlaceholder: {
        color: '#9ca3af',
      },
      icon: {
        color: '#9ca3af',
      },
      iconHover: {
        color: '#3b82f6',
      },
      clearButton: {
        color: '#9ca3af',
        backgroundColor: 'transparent',
      },
      clearButtonHover: {
        color: '#ef4444',
      },
    },
  };

  const currentSize = sizes[size] || sizes.md;
  const currentVariant = variants[variant] || variants.default;

  const styles = {
    container: {
      display: 'flex',
      alignItems: 'center',
      position: 'relative',
      width: fullWidth ? '100%' : 'auto',
      borderRadius: variant === 'underlined' ? '0' : '0.375rem',
      transition: 'all 0.2s',
      ...currentSize.container,
      ...currentVariant.container,
      ...(isFocused && !disabled ? currentVariant.containerFocused : {}),
      ...(disabled && {
        opacity: 0.6,
        cursor: 'not-allowed',
        backgroundColor: '#f9fafb',
      }),
    },
    input: {
      width: '100%',
      height: '100%',
      border: 'none',
      outline: 'none',
      background: 'transparent',
      ...currentSize.input,
      ...currentVariant.input,
      ...(disabled && {
        cursor: 'not-allowed',
      }),
    },
    iconContainer: {
      position: 'absolute',
      left: 0,
      top: 0,
      bottom: 0,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      paddingLeft: size === 'sm' ? '0.5rem' : size === 'lg' ? '1rem' : '0.75rem',
      pointerEvents: 'none',
    },
    icon: {
      ...currentSize.icon,
      ...currentVariant.icon,
      transition: 'color 0.2s',
    },
    clearButton: {
      position: 'absolute',
      right: showFilterButton ? '2.5rem' : '0.5rem',
      top: '50%',
      transform: 'translateY(-50%)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      border: 'none',
      borderRadius: '9999px',
      cursor: disabled ? 'not-allowed' : 'pointer',
      transition: 'all 0.2s',
      ...currentSize.clearButton,
      ...currentVariant.clearButton,
      ...(disabled && {
        cursor: 'not-allowed',
        opacity: 0.5,
      }),
    },
    filterButton: {
      position: 'absolute',
      right: '0.5rem',
      top: '50%',
      transform: 'translateY(-50%)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '0.25rem',
      border: variant === 'underlined' ? 'none' : '1px solid #d1d5db',
      borderRadius: '0.375rem',
      background: 'transparent',
      cursor: disabled ? 'not-allowed' : 'pointer',
      transition: 'all 0.2s',
      ...currentSize.filterButton,
      ...(disabled && {
        cursor: 'not-allowed',
        opacity: 0.5,
      }),
    },
    filterButtonHover: {
      backgroundColor: '#f3f4f6',
    },
    filterCount: {
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      minWidth: '1.25rem',
      height: '1.25rem',
      padding: '0 0.25rem',
      backgroundColor: '#3b82f6',
      color: '#ffffff',
      fontSize: '0.75rem',
      fontWeight: 600,
      borderRadius: '9999px',
      marginLeft: '0.25rem',
    },
    suggestions: {
      position: 'absolute',
      top: '100%',
      left: 0,
      right: 0,
      marginTop: '0.25rem',
      backgroundColor: '#ffffff',
      border: '1px solid #e5e7eb',
      borderRadius: '0.375rem',
      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
      zIndex: 50,
      maxHeight: '15rem',
      overflowY: 'auto',
    },
    suggestionItem: {
      padding: '0.5rem 1rem',
      cursor: 'pointer',
      transition: 'background-color 0.2s',
      fontSize: currentSize.input.fontSize,
      color: '#374151',
    },
    suggestionItemHover: {
      backgroundColor: '#f3f4f6',
    },
    suggestionItemActive: {
      backgroundColor: '#eff6ff',
    },
    recentSearch: {
      padding: '0.5rem 1rem',
      borderBottom: '1px solid #e5e7eb',
      fontSize: currentSize.input.fontSize,
      color: '#6b7280',
      fontWeight: 500,
    },
  };

  // Debounced search
  useEffect(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }

    timerRef.current = setTimeout(() => {
      onSearch(searchTerm);
    }, delay);

    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, [searchTerm, delay, onSearch]);

  // Auto focus
  useEffect(() => {
    if (autoFocus && inputRef.current) {
      inputRef.current.focus();
    }
  }, [autoFocus]);

  const handleChange = useCallback((e) => {
    setSearchTerm(e.target.value);
  }, []);

  const handleClear = useCallback(() => {
    setSearchTerm('');
    onSearch('');
    if (inputRef.current) {
      inputRef.current.focus();
    }
  }, [onSearch]);

  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Escape') {
      handleClear();
    }
    if (e.key === 'Enter') {
      // Trigger immediate search on Enter
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
      onSearch(searchTerm);
    }
  }, [searchTerm, onSearch, handleClear]);

  const handleSuggestionClick = useCallback((suggestion) => {
    setSearchTerm(suggestion);
    onSearch(suggestion);
  }, [onSearch]);

  return (
    <div style={styles.container}>
      {/* Search Icon */}
      {showSearchIcon && (
        <div style={styles.iconContainer}>
          <svg style={styles.icon} viewBox="0 0 24 24" fill="none" stroke="currentColor">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
            />
          </svg>
        </div>
      )}

      {/* Input */}
      <input
        ref={inputRef}
        type="text"
        value={searchTerm}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        onFocus={() => setIsFocused(true)}
        onBlur={() => setIsFocused(false)}
        placeholder={placeholder}
        disabled={disabled}
        style={styles.input}
      />

      {/* Clear Button */}
      {showClearButton && searchTerm && !disabled && (
        <button
          onClick={handleClear}
          style={styles.clearButton}
          onMouseEnter={(e) => {
            e.currentTarget.style.color = currentVariant.clearButtonHover.color;
            e.currentTarget.style.backgroundColor = currentVariant.clearButtonHover.backgroundColor;
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.color = currentVariant.clearButton.color;
            e.currentTarget.style.backgroundColor = currentVariant.clearButton.backgroundColor;
          }}
          aria-label="Clear search"
        >
          ×
        </button>
      )}

      {/* Filter Button */}
      {showFilterButton && !disabled && (
        <button
          onClick={onFilterClick}
          style={styles.filterButton}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = styles.filterButtonHover.backgroundColor;
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = 'transparent';
          }}
          aria-label="Open filters"
        >
          <svg style={currentSize.icon} viewBox="0 0 24 24" fill="none" stroke="currentColor">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z"
            />
          </svg>
          {filterCount > 0 && (
            <span style={styles.filterCount}>{filterCount}</span>
          )}
        </button>
      )}

      {/* Add keyframe animations */}
      <style jsx>{`
        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translateY(-10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        @keyframes pulse {
          0%, 100% {
            opacity: 1;
          }
          50% {
            opacity: 0.5;
          }
        }
      `}</style>
    </div>
  );
};

// Export additional components for more complex search scenarios
SearchBar.Suggestions = ({ suggestions, onSelect, searchTerm }) => {
  const styles = {
    container: {
      position: 'absolute',
      top: '100%',
      left: 0,
      right: 0,
      marginTop: '0.25rem',
      backgroundColor: '#ffffff',
      border: '1px solid #e5e7eb',
      borderRadius: '0.375rem',
      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
      zIndex: 50,
      maxHeight: '15rem',
      overflowY: 'auto',
      animation: 'fadeIn 0.2s ease-out',
    },
    item: {
      padding: '0.5rem 1rem',
      cursor: 'pointer',
      transition: 'background-color 0.2s',
      fontSize: '0.875rem',
      color: '#374151',
    },
    highlight: {
      backgroundColor: '#f3f4f6',
    },
    noResults: {
      padding: '0.5rem 1rem',
      color: '#6b7280',
      fontSize: '0.875rem',
      textAlign: 'center',
    },
  };

  const highlightText = (text, highlight) => {
    if (!highlight) return text;
    const parts = text.split(new RegExp(`(${highlight})`, 'gi'));
    return parts.map((part, i) => 
      part.toLowerCase() === highlight.toLowerCase() ? 
        <strong key={i} style={{ backgroundColor: '#fef3c7' }}>{part}</strong> : 
        part
    );
  };

  if (!suggestions || suggestions.length === 0) {
    return (
      <div style={styles.container}>
        <div style={styles.noResults}>No suggestions found</div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <div style={{ padding: '0.5rem 1rem', borderBottom: '1px solid #e5e7eb', fontSize: '0.75rem', color: '#6b7280' }}>
        Suggestions
      </div>
      {suggestions.map((suggestion, index) => (
        <div
          key={index}
          style={styles.item}
          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f3f4f6'}
          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
          onClick={() => onSelect(suggestion)}
        >
          {highlightText(suggestion, searchTerm)}
        </div>
      ))}
    </div>
  );
};

SearchBar.RecentSearches = ({ searches, onSelect, onClear }) => {
  const styles = {
    container: {
      position: 'absolute',
      top: '100%',
      left: 0,
      right: 0,
      marginTop: '0.25rem',
      backgroundColor: '#ffffff',
      border: '1px solid #e5e7eb',
      borderRadius: '0.375rem',
      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
      zIndex: 50,
      animation: 'fadeIn 0.2s ease-out',
    },
    header: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      padding: '0.5rem 1rem',
      borderBottom: '1px solid #e5e7eb',
    },
    title: {
      fontSize: '0.75rem',
      color: '#6b7280',
      fontWeight: 500,
    },
    clearButton: {
      fontSize: '0.75rem',
      color: '#3b82f6',
      background: 'none',
      border: 'none',
      cursor: 'pointer',
      padding: '0.25rem 0.5rem',
      borderRadius: '0.25rem',
    },
    item: {
      padding: '0.5rem 1rem',
      cursor: 'pointer',
      transition: 'background-color 0.2s',
      fontSize: '0.875rem',
      color: '#374151',
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem',
    },
    icon: {
      width: '1rem',
      height: '1rem',
      color: '#9ca3af',
    },
  };

  if (!searches || searches.length === 0) {
    return null;
  }

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <span style={styles.title}>Recent Searches</span>
        <button style={styles.clearButton} onClick={onClear}>
          Clear all
        </button>
      </div>
      {searches.map((search, index) => (
        <div
          key={index}
          style={styles.item}
          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f3f4f6'}
          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
          onClick={() => onSelect(search)}
        >
          <svg style={styles.icon} viewBox="0 0 24 24" fill="none" stroke="currentColor">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
          {search}
        </div>
      ))}
    </div>
  );
};

export default SearchBar;